/**
 * RecentMeetingPill — floating "Just saved" indicator with a one-click
 * jump back to the Board card the user just made.
 *
 * Why this exists:
 *   • The Sit-In flow records → parses → saves a meeting to the Board.
 *   • If the user navigates AWAY from the Board (most commonly to the
 *     Map via an address chip in the results view), they lose visual
 *     continuity with the meeting they just made. There's a BOARD
 *     button in the top nav, but nothing tells them "the meeting you
 *     just made is over there" — and finding the specific card on a
 *     busy board takes a beat.
 *
 * What it does:
 *   • Reads `coast.last_saved_meeting` from localStorage on mount and
 *     on `coast:last-meeting-changed` events.
 *   • If a meeting was saved within the last 10 minutes AND we're
 *     not currently on /board, render a floating pill bottom-left
 *     with the meeting's title.
 *   • Clicking the pill navigates to /board?focus=<id> — Board uses
 *     this to scroll the just-made card into view + flash-highlight it.
 *   • Has a dismiss × that hides the pill for the current saved
 *     meeting only. A new save re-shows it.
 *   • Auto-fades after 10 minutes.
 */
import React, { useEffect, useState, useCallback } from 'react';

// Coast's deployed Dashboard palette (matches SitInFab + light-theme modals).
const ACCENT     = '#b8956a';
const SURFACE    = '#FAFAF8';
const BORDER     = '#E6DDD4';
const TEXT       = '#0D3D5C';
const TEXT_DIM   = '#6B7280';

const STALE_MS = 10 * 60 * 1000;        // pill auto-fades after 10 min
const LS_KEY   = 'coast.last_saved_meeting';
const LS_DISMISS_KEY = 'coast.last_saved_meeting.dismissed_id';

interface SavedMarker {
  id:          string;
  title:       string;
  recorded_at: string;
  saved_at:    number;
}

export default function RecentMeetingPill() {
  const [marker, setMarker] = useState<SavedMarker | null>(null);
  const [route, setRoute]   = useState(window.location.pathname);

  const refresh = useCallback(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) { setMarker(null); return; }
      const m = JSON.parse(raw) as SavedMarker;
      if (!m || !m.saved_at) { setMarker(null); return; }
      if (Date.now() - m.saved_at > STALE_MS) {
        // Stale. Drop the marker so it doesn't reappear on next refresh.
        localStorage.removeItem(LS_KEY);
        setMarker(null);
        return;
      }
      const dismissed = localStorage.getItem(LS_DISMISS_KEY);
      if (dismissed === m.id) { setMarker(null); return; }
      setMarker(m);
    } catch {
      setMarker(null);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    const onNav    = () => setRoute(window.location.pathname);
    window.addEventListener('coast:last-meeting-changed', onChange);
    window.addEventListener('popstate', onNav);
    // Storage event fires when other tabs change LS — covers the
    // multi-tab case where one tab saves while another is mounted.
    window.addEventListener('storage', onChange);
    // Re-check periodically so we naturally fade out after STALE_MS
    // even if no user action triggers refresh.
    const t = window.setInterval(refresh, 30_000);
    return () => {
      window.removeEventListener('coast:last-meeting-changed', onChange);
      window.removeEventListener('popstate', onNav);
      window.removeEventListener('storage', onChange);
      window.clearInterval(t);
    };
  }, [refresh]);

  // Hide on Board itself — the user is already there.
  if (!marker || route === '/board') return null;

  const goToBoard = () => {
    const url = `/board?focus=${encodeURIComponent(marker.id)}`;
    window.history.pushState({}, '', url);
    window.dispatchEvent(new Event('popstate'));
  };

  const dismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    try { localStorage.setItem(LS_DISMISS_KEY, marker.id); } catch {}
    setMarker(null);
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: 24, left: 24,
      maxWidth: 380,
      background: SURFACE,
      border: `1px solid ${ACCENT}`,
      borderRadius: 6,
      padding: '12px 14px',
      boxShadow: '0 8px 28px rgba(0,0,0,0.15)',
      fontFamily: '"EB Garamond", Georgia, serif',
      color: TEXT,
      zIndex: 6500,    // below SitInFab (7000), above page content
      display: 'flex', alignItems: 'flex-start', gap: 10,
      cursor: 'pointer',
      animation: 'recentPillIn 0.32s cubic-bezier(0.16, 1, 0.3, 1)',
    }} onClick={goToBoard}>
      <div style={{
        width: 8, height: 8, borderRadius: '50%',
        background: ACCENT, marginTop: 6, flexShrink: 0,
        boxShadow: `0 0 0 4px ${ACCENT}25`,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 9, color: ACCENT, letterSpacing: '0.16em',
          textTransform: 'uppercase', fontWeight: 700,
        }}>
          Just saved
        </div>
        <div style={{
          fontFamily: 'Fraunces, Georgia, serif',
          fontSize: 14, color: TEXT, lineHeight: 1.35,
          marginTop: 3,
          // Truncate long meeting summaries to one line — full text is
          // available on the Board card itself.
          overflow: 'hidden', textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical' as any,
        }}>
          {marker.title}
        </div>
        <div style={{
          fontSize: 10, color: TEXT_DIM, marginTop: 4,
          letterSpacing: '0.04em',
        }}>
          Click to view on Board →
        </div>
      </div>
      <button
        onClick={dismiss}
        title="Dismiss"
        style={{
          background: 'transparent', border: 'none', color: TEXT_DIM,
          fontSize: 18, cursor: 'pointer', padding: 0, lineHeight: 1,
          marginLeft: 4, marginTop: -2, flexShrink: 0,
        }}>×</button>
      <style>{`
        @keyframes recentPillIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
